import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Product } from '@/types';
import { useLanguage } from '@/context/LanguageContext';

// Base URL configuration
const BASE_URL = import.meta.env.VITE_SITE_URL || 'https://luxelo.web.app';
const SITE_NAME = 'Luxelo';
const DEFAULT_IMAGE = `${BASE_URL}/img/luxelo-high-resolution-logo.png`;

export type Language = 'pl' | 'en' | 'de' | 'it' | 'fr' | 'es';
export const SUPPORTED_LANGUAGES: Language[] = ['pl', 'en', 'de', 'it', 'fr', 'es'];

const LANGUAGE_LOCALES: Record<Language, string> = {
  pl: 'pl_PL',
  en: 'en_US',
  de: 'de_DE',
  it: 'it_IT',
  fr: 'fr_FR',
  es: 'es_ES',
};

export interface SEOData {
  title: string;
  description: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  product?: Product;
  noindex?: boolean;
  canonical?: string;
  structuredData?: object | object[];
}

interface SEOElements {
  title: string | null;
  metaTags: Map<string, HTMLMetaElement>;
  linkTags: Map<string, HTMLLinkElement>;
  scriptTags: HTMLScriptElement[];
}

/**
 * Custom hook for managing SEO in a React SPA
 * Updates document head with proper meta tags, Open Graph, Twitter cards, and JSON-LD structured data
 */
export function useSEO(seoData: SEOData) {
  const location = useLocation();
  const { language } = useLanguage();
  const previousElementsRef = useRef<SEOElements>({
    title: null,
    metaTags: new Map(),
    linkTags: new Map(),
    scriptTags: [],
  });

  useEffect(() => {
    const {
      title,
      description,
      image = DEFAULT_IMAGE,
      type = 'website',
      product,
      noindex = false,
      canonical,
      structuredData,
    } = seoData;

    const currentUrl = canonical || `${BASE_URL}${location.pathname}`;
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const locale = LANGUAGE_LOCALES[language as Language] || 'en_US';

    // Store original title
    if (previousElementsRef.current.title === null) {
      previousElementsRef.current.title = document.title;
    }

    // Update document title
    document.title = fullTitle;

    // Helper to set or create meta tag
    const setMetaTag = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
        previousElementsRef.current.metaTags.set(name, meta);
      }
      
      meta.setAttribute('content', content);
    };

    // Helper to set or create link tag
    const setLinkTag = (rel: string, href: string, hreflang?: string) => {
      const key = hreflang ? `${rel}-${hreflang}` : rel;
      let link = hreflang 
        ? document.querySelector(`link[rel="${rel}"][hreflang="${hreflang}"]`) as HTMLLinkElement
        : document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        if (hreflang) {
          link.setAttribute('hreflang', hreflang);
        }
        document.head.appendChild(link);
        previousElementsRef.current.linkTags.set(key, link);
      }
      
      link.setAttribute('href', href);
    };

    // Basic meta tags
    setMetaTag('description', description);
    setMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph tags
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:url', currentUrl, true);
    setMetaTag('og:type', type === 'product' ? 'website' : type, true);
    setMetaTag('og:site_name', SITE_NAME, true);
    setMetaTag('og:image', image, true);
    setMetaTag('og:image:width', '1200', true);
    setMetaTag('og:image:height', '630', true);
    setMetaTag('og:locale', locale, true);

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', fullTitle);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', image);

    // Canonical link
    setLinkTag('canonical', currentUrl);

    // Hreflang links for multilingual support
    SUPPORTED_LANGUAGES.forEach(lang => {
      const langUrl = `${BASE_URL}/${lang}${location.pathname}`;
      setLinkTag('alternate', langUrl, lang);
    });
    // x-default fallback
    setLinkTag('alternate', `${BASE_URL}${location.pathname}`, 'x-default');

    // JSON-LD Structured Data
    // Remove previous structured data scripts
    previousElementsRef.current.scriptTags.forEach(script => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    });
    previousElementsRef.current.scriptTags = [];

    // Add new structured data
    const structuredDataArray = Array.isArray(structuredData) 
      ? structuredData 
      : structuredData 
        ? [structuredData] 
        : [];

    // If product, add product structured data
    if (product && type === 'product') {
      const productSchema = generateProductStructuredData(product, currentUrl, language as Language);
      structuredDataArray.push(productSchema);
    }

    // Add organization and website structured data on homepage
    if (location.pathname === '/' || location.pathname === '') {
      structuredDataArray.push(generateOrganizationStructuredData());
      structuredDataArray.push(generateWebsiteStructuredData());
    }

    // Inject structured data scripts
    structuredDataArray.forEach((data) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
      previousElementsRef.current.scriptTags.push(script);
    });

    // Cleanup on unmount or when SEO data changes
    return () => {
      // We don't remove tags on route change to avoid flashing
      // They will be updated with new values
    };
  }, [seoData, location.pathname, language]);
}

/**
 * Generate Product structured data (JSON-LD)
 */
function generateProductStructuredData(
  product: Product, 
  url: string,
  lang: Language = 'en'
): object {
  const availability = product.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';
  
  // Get language-specific content
  const productLang = product.translations?.[lang] || product.translations?.['en'] || {};
  const productName = product.nameTranslations?.[lang] || product.name;
  const productDescription = productLang.shortDescription || productLang.description || product.shortDescription || product.description;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productName,
    description: productDescription,
    image: product.images || [],
    sku: product.id,
    mpn: product.id,
    brand: {
      '@type': 'Brand',
      name: SITE_NAME,
    },
    offers: {
      '@type': 'Offer',
      url: url,
      priceCurrency: 'PLN',
      price: product.price.toString(),
      availability: availability,
      priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      itemCondition: 'https://schema.org/NewCondition',
      seller: {
        '@type': 'Organization',
        name: SITE_NAME,
      },
    },
    inLanguage: lang,
    ...(product.attributes && Object.keys(product.attributes).length > 0 && {
      additionalProperty: Object.entries(product.attributes).map(([, attr]) => ({
        '@type': 'PropertyValue',
        name: attr.label,
        value: `${attr.value}${attr.unit ? ` ${attr.unit}` : ''}`,
      })),
    }),
  };
}

/**
 * Generate Organization structured data (JSON-LD)
 */
function generateOrganizationStructuredData(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: BASE_URL,
    logo: `${BASE_URL}/img/luxelo-high-resolution-logo.png`,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+48-123-456-789',
      contactType: 'customer service',
      email: 'contact@luxelo.de',
      availableLanguage: SUPPORTED_LANGUAGES,
    },
  };
}

/**
 * Generate Website structured data (JSON-LD)
 */
function generateWebsiteStructuredData(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: BASE_URL,
    inLanguage: SUPPORTED_LANGUAGES,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Generate BreadcrumbList structured data (JSON-LD)
 */
export function generateBreadcrumbStructuredData(
  items: Array<{ name: string; url: string }>
): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Generate FAQ structured data (JSON-LD)
 */
export function generateFAQStructuredData(
  faqItems: Array<{ question: string; answer: string }>
): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

export default useSEO;

